import type { ResolvedChartWidget } from "@t3tools/contracts";
import type {
  ChartData,
  ChartDataset,
  ChartOptions,
  ScriptableContext,
  ScriptableLineSegmentContext,
  TooltipItem,
} from "chart.js";

import { artifactColumnLabel, formatArtifactNumber, formatArtifactValue } from "./artifactFormat";
import type { ArtifactColumn, ArtifactWidget } from "./artifactTypes";

export type ArtifactChartWidget = Extract<ArtifactWidget, { kind: "chart" }>;

export const ARTIFACT_CHART_PALETTE = [
  "#0F9F8F",
  "#2563EB",
  "#F3B35B",
  "#F7941D",
  "#7C3AED",
  "#E11D48",
  "#64748B",
  "#84CC16",
] as const;
const MUTED = "#6D6A66";
const GRID = "#EDE9E4";

export type ArtifactChartType = "bar" | "line";
type ArtifactChartDataset = ChartDataset<ArtifactChartType, Array<number | null>>;
export type ArtifactChartConfig = {
  type: ArtifactChartType;
  data: ChartData<ArtifactChartType, Array<number | null>, string>;
  options: ChartOptions<ArtifactChartType>;
};

/**
 * Builds Chart.js data and options for a chart widget from its resolved data.
 * Points at or after `settlingFrom` (an x index) are drawn hollow and dashed:
 * their period may still change.
 */
export function buildArtifactChart(
  widget: ArtifactChartWidget,
  resolved: ResolvedChartWidget,
  columns: ReadonlyMap<string, ArtifactColumn>,
  options: { settlingFrom?: number | undefined } = {},
): ArtifactChartConfig {
  const settlingFrom = options.settlingFrom ?? Number.POSITIVE_INFINITY;
  const settling = (index: number) => index >= settlingFrom;
  const type: ArtifactChartType =
    widget.chart === "line" || widget.chart === "area" ? "line" : "bar";
  const stacked = widget.chart === "stacked_bar";
  const horizontal = widget.orientation === "horizontal";
  const xColumn = columns.get(resolved.x.column);
  const labels = resolved.x.values.map((value) =>
    formatArtifactValue(value, xColumn, { short: true }),
  );
  const formatByAxis = new Map<string, ArtifactColumn["format"]>();

  const datasets = resolved.series.map((series, index): ArtifactChartDataset => {
    const color = ARTIFACT_CHART_PALETTE[index % ARTIFACT_CHART_PALETTE.length]!;
    const axisId = series.axis === "right" ? "y2" : "y";
    if (!formatByAxis.has(axisId)) formatByAxis.set(axisId, columns.get(series.column)?.format);
    const base = {
      label:
        "group" in series
          ? formatArtifactValue(
              series.group,
              widget.groupBy ? columns.get(widget.groupBy) : undefined,
            )
          : (series.label ?? artifactColumnLabel(columns.get(series.column), series.column)),
      data: [...series.values],
      yAxisID: horizontal ? undefined : axisId,
      ...(horizontal ? { xAxisID: "x" } : {}),
    };
    if (series.type === "line") {
      return {
        ...base,
        type: "line",
        borderColor: color,
        backgroundColor: widget.chart === "area" ? `${color}33` : color,
        pointBackgroundColor: (context: ScriptableContext<"line">) =>
          settling(context.dataIndex) ? "transparent" : color,
        pointBorderColor: color,
        pointRadius: (context: ScriptableContext<"line">) => (settling(context.dataIndex) ? 3 : 2),
        segment: {
          borderDash: (context: ScriptableLineSegmentContext) =>
            settling(context.p1DataIndex) ? [4, 4] : undefined,
        },
        borderWidth: 2,
        fill: widget.chart === "area",
        tension: 0.25,
        order: 1,
      } as ArtifactChartDataset;
    }
    return {
      ...base,
      type: "bar",
      backgroundColor: (context: ScriptableContext<"bar">) =>
        settling(context.dataIndex) ? `${color}80` : color,
      borderRadius: stacked ? 0 : 3,
      order: 2,
    } as ArtifactChartDataset;
  });

  const valueTicks = (axisId: string) => ({
    color: MUTED,
    font: { size: 10 },
    callback: (value: string | number) =>
      formatArtifactNumber(Number(value), formatByAxis.get(axisId)),
  });
  const categoryScale = {
    stacked,
    grid: { display: false },
    ticks: { color: MUTED, font: { size: 10 }, maxRotation: 0, autoSkip: true },
  };
  const valueScale = { stacked, grid: { color: GRID }, beginAtZero: true, ticks: valueTicks("y") };
  const scales: Record<string, unknown> = horizontal
    ? { x: { ...valueScale, ticks: valueTicks("y") }, y: categoryScale }
    : { x: categoryScale, y: valueScale };
  if (!horizontal && formatByAxis.has("y2")) {
    scales.y2 = {
      position: "right",
      beginAtZero: true,
      grid: { display: false },
      ticks: valueTicks("y2"),
    };
  }

  return {
    type,
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: horizontal ? "y" : "x",
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          display: datasets.length > 1,
          position: "bottom",
          labels: { boxWidth: 10, color: MUTED, font: { size: 11 } },
        },
        tooltip: {
          callbacks: {
            label: (item: TooltipItem<ArtifactChartType>) => {
              const axisId = (item.dataset as { yAxisID?: string }).yAxisID ?? "y";
              const raw = horizontal ? item.parsed.x : item.parsed.y;
              const value =
                raw === null || raw === undefined
                  ? "—"
                  : formatArtifactNumber(raw, formatByAxis.get(axisId));
              return `${item.dataset.label ?? ""}: ${value}`;
            },
            footer: (items: Array<TooltipItem<ArtifactChartType>>) =>
              items.some((item) => settling(item.dataIndex)) ? "Settling: may still change" : "",
          },
        },
      },
      scales: scales as NonNullable<ChartOptions<ArtifactChartType>["scales"]>,
    },
  };
}
